import React from 'react';
import { Box, Typography, Paper } from '@mui/material';

interface ScoreGaugeProps {
  label: string;
  value: number | string | null | undefined;
  maxValue?: number;
  valueDirection?: string; // "Lower is Better" or "Higher is Better"
}

const ScoreGauge: React.FC<ScoreGaugeProps> = ({ 
  label, 
  value,
  maxValue = 99999,
  valueDirection
}) => {
  // Convert value to number if it's a string
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // If no valid value, show N/A
  if (numericValue === null || numericValue === undefined || isNaN(numericValue)) {
    return (
      <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
            {label}
          </Typography>
        </Box>
        {/* Scale bar */}
        <Box sx={{ position: 'relative', height: 60 }}>
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 12,
            background: 'linear-gradient(to right, #f44336, #ffba1c, #66bb6a, #2196f3)',
            borderRadius: 1,
            border: '1px solid #ddd'
          }} />
        </Box>
        {/* Display range labels */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999', fontWeight: 600 }}>00000</Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999', fontWeight: 600 }}>99999</Typography>
        </Box>
      </Paper>
    );
  }

  // Calculate position as percentage of max value
  const rawPercentage = Math.min((numericValue / maxValue) * 100, 100);
  // Apply minimum offset - arrow won't go below 5% from the left
  const percentage = Math.max(rawPercentage, 5);
  
  // Determine color based on position
  const getColor = () => {
    if (percentage < 25) return '#f44336'; // Red
    if (percentage < 50) return '#ffba1c'; // Yellow/Orange
    if (percentage < 75) return '#66bb6a'; // Light Green
    return '#2196f3'; // Blue
  };

  const color = getColor();

  return (
    <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
          {label}
        </Typography>
      </Box>
      
      {/* Gauge scale */}
      <Box sx={{ position: 'relative', height: 60 }}>
        {/* Gradient background scale */}
        <Box sx={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 12,
          background: 'linear-gradient(to right, #f44336, #ffba1c, #66bb6a, #2196f3)',
          borderRadius: 1,
          border: '1px solid #ddd'
        }} />
        
        {/* Arrow indicator - INVERTED (pointing down) */}
        <Box sx={{
          position: 'absolute',
          top: 12,
          left: `${percentage}%`,
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 10
        }}>
          {/* Arrow pointing down */}
          <Box sx={{
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: `8px solid ${color}`,
          }} />
          {/* Value below arrow */}
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.95rem', 
              fontWeight: 700, 
              color: color,
              mt: 0.5
            }}
          >
            {numericValue.toFixed(0)}
          </Typography>
        </Box>
      </Box>
      
      {/* Display range labels with direction in middle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, px: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999', fontWeight: 600 }}>00000</Typography>
        {valueDirection && (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.65rem', 
              color: '#666',
              fontStyle: 'italic',
              textAlign: 'center'
            }}
          >
            {valueDirection}
          </Typography>
        )}
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999', fontWeight: 600 }}>99999</Typography>
      </Box>
    </Paper>
  );
};

export default ScoreGauge;
